import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { mdiArrowRight } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Checkbox } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useMonitoringOffersStore } from 'services/monitoring-offers';
import { useUsersStore } from 'services/users';
import CardHeader from './components/CardHeader';
import CardPhase from './components/CardPhase';
import CardSpecialistInfo from './components/CardSpecialistInfo';
import CardFarmerInfo from './components/CardFarmerInfo';

interface MonitoringOngoingCardProps {
	data: any;
	onChat?: (monitoringId: string) => void;
	chatLoading?: boolean;
	isAdmin?: boolean;
	isSelected?: boolean;
	onCheckboxChange?: (id: string, event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const MonitoringOngoingCard = ({
	data,
	onChat,
	chatLoading,
	isAdmin = false,
	isSelected = false,
	onCheckboxChange,
}: MonitoringOngoingCardProps) => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { setSelectedOffer } = useMonitoringOffersStore();
	const isCompleted = moment(data?.end_date).isBefore(moment());
	const { user } = useUsersStore();

	const handleSelectOffer = () => {
		setSelectedOffer(data);
		localStorage.setItem('selectedOffer', JSON.stringify(data));
		navigateWithLanguage(`/${user?.role.toLowerCase()}/monitoring/project/${data?.id}`);
	};

	const handleCheckboxClick = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	return (
		<div className="rounded-lg shadow-md w-full bg-white relative border-[3px] border-primary flex flex-col justify-between h-full">
			{isAdmin && (
				<div className="absolute top-0 right-0 z-20">
					<Checkbox
						checked={isSelected}
						onChange={(e) => onCheckboxChange && onCheckboxChange(data?.id, e)}
						sx={{
							color: '#2196F3', // Bright blue for visibility
							padding: '8px',
							'&.Mui-checked': {
								color: '#2196F3',
							},
							'& .MuiSvgIcon-root': { 
								fontSize: '28px' // Make the checkbox bigger
							}
						}}
						onClick={handleCheckboxClick}
					/>
				</div>
			)}
			<div className="flex flex-col">
				<CardHeader
					price={data?.services.price}
					phases={data?.services.phases}
					title={data?.crop?.name}
					monitoringType={t('standardMonitoring')}
					image={data?.crop?.image_url}
					isCompleted={isCompleted}
					payment_status={data?.payment_status}
					invoice_url={data?.invoice_url}
				/>
				<div className="w-full h-[1px] bg-light " />
				{user?.role?.toLowerCase() === 'specialist' && data?.farmer && (
					<CardFarmerInfo
						farmer={data?.farmer}
						channel={data?.channel}
						monitoringId={data?.id}
						onChat={onChat}
						chatLoading={chatLoading}
					/>
				)}
				{data?.specialist && (
					<CardSpecialistInfo
						specialist={data?.specialist}
						channel={data?.channel}
						monitoringId={data?.id}
						onChat={onChat}
						chatLoading={chatLoading}
					/>
				)}
			</div>
			<div className="flex flex-col">
				<CardPhase
					phases_management={data?.phases_management?.active[0]}
					isCompleted={isCompleted}
				/>
				<div className="flex items-center justify-center w-full p-4">
					<Button
						variant="contained"
						onClick={handleSelectOffer}
						fullWidth
						sx={{
							'@media (max-width: 400px)': {
								fontSize: '12px',
							},
						}}
						endIcon={<Icon path={mdiArrowRight} size={1} />}
					>
						{isCompleted ? t('requestsAndOffers.completedProject') : t('monitoringProtocols')}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default MonitoringOngoingCard;
